<script setup>
import { defineEmits, defineProps } from "vue";
import { useI18n } from "vue-i18n";
import { ref, reactive } from 'vue';
import { to } from "await-to-js";
import { useEnvStore, interceptor, useTwoWayBinding } from "@dutypay/front-end-design-system";
import { useOneTimePasswordValidation, useMobileNumberValidation } from "@dutypay/front-end-design-system/src/validations";

const { t } = useI18n();
const emit = defineEmits(["update", "trigger-authenticate"]);
const props = defineProps({
  modelValue: { type: Object, required: true },
});
const loginData = useTwoWayBinding(props, emit, "modelValue");
const mobileNumberAvailable = ref(false);
const resendDisabled = ref(true);
const notification = reactive({
  sendSMS: {
    success: false,
    asyncError: false
  },
  saveMobileNumber: {
    asyncError: false
  }
});
const mobileNumberForm = reactive({
  mobileNumber: ""
})
const isLoadingMobileNumberForm = ref(false);
const countdown = ref(0);
const envStore = useEnvStore();

async function setupSMSAuthentication() {
  loginData.value.firstTime = true;
  loginData.value.mode = "sms";
  emit("trigger-authenticate");
}

async function saveMobileNumber() {
  isLoadingMobileNumberForm.value = true;
  notification.saveMobileNumber.asyncError = false;
  let err;
  [err] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/setup/auth-sms`, {
    mobileNumber: mobileNumberForm.mobileNumber,
    ...loginData.value,
  }));
  if (err) {
    isLoadingMobileNumberForm.value = false;
    notification.saveMobileNumber.asyncError = true;
    return;
  }
  notification.saveMobileNumber.asyncError = false;
  await sendAuthenticationSMS();
  isLoadingMobileNumberForm.value = false;
}

function countDownTimer() {
  if (countdown.value > 0) {
    setTimeout(() => {
      countdown.value -= 1;
      countDownTimer();
    }, 1000);
  } else {
    resendDisabled.value = false;
  }
}
async function sendAuthenticationSMS() {
  resendDisabled.value = true;
  notification.sendSMS.success = false;
  notification.sendSMS.asyncError = false;
  let err;
  [err] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/send/auth-sms`, {
    ...loginData.value,
  }));
  if (err) {
    mobileNumberAvailable.value = false
    resendDisabled.value = false;
    notification.sendSMS.asyncError = true;
  } else {
    notification.sendSMS.success = true;
    notification.sendSMS.asyncError = false;
    mobileNumberAvailable.value = true;
    countdown.value = 20;
    countDownTimer();
  }
}

const validation = {
  authenticatorMobileNumber: [
    useMobileNumberValidation(),
  ],
  SMSOTP: [
    useOneTimePasswordValidation()
  ],
}

</script>
<script>

export default {
  name: "SMSSetupForm"
}
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.SMSSetupForm.SMSSetupForm.title") }}</h2>

    <p>{{ t("sso.pages.LoginStages.SMSSetupForm.pleaseEnterMobileNumber.label") }}</p>

    <DpNotification
      :visibility="notification.sendSMS.success"
      :title="t('sso.pages.LoginStages.SMSSetupForm.notification.sendSMS.success.title')"
      :message="t('sso.pages.LoginStages.SMSSetupForm.notification.sendSMS.success.message', { mobileNumberPlaceholder: mobileNumberForm.mobileNumber })"
      type="success"
    ></DpNotification>

    <DpNotification
      :visibility="notification.sendSMS.asyncError"
      :title="t('sso.pages.LoginStages.SMSSetupForm.notification.sendSMS.asyncError.title')"
      :message="t('sso.pages.LoginStages.SMSSetupForm.notification.sendSMS.asyncError.message')"
      type="error"
    ></DpNotification>

    <DpNotification
      :visibility="notification.saveMobileNumber.asyncError"
      :title="t('sso.pages.LoginStages.SMSSetupForm.notification.saveMobileNumber.asyncError.title')"
      :message="t('sso.pages.LoginStages.SMSSetupForm.notification.saveMobileNumber.asyncError.message')"
      type="error"
    ></DpNotification>

    <DpExpand>
      <DpForm
        v-show="!mobileNumberAvailable"
        @submit="saveMobileNumber"
        :showValidationNotification="false"
        :validation="validation.authenticatorMobileNumber"
        v-model="mobileNumberForm"
        v-loading="isLoadingMobileNumberForm"
        >
        <DpInputPhone
          v-model="mobileNumberForm.mobileNumber"
          :validation="validation.authenticatorMobileNumber"
          id="mobileNumber"
          autocomplete="off"
          :label="t('sso.pages.LoginStages.SMSSetupForm.mobileNumber.input.label')"
          :placeholder="t('sso.pages.LoginStages.SMSSetupForm.mobileNumber.input.placeholder')"
          />
        <template #control="{ validate }">
        <DpButton
          purpose="primaryAction"
          size="large"
          @click="validate">
            {{ t("sso.pages.LoginStages.SMSSetupForm.saveMobileNumber.buttonLabel") }}
        </DpButton>
      </template>
    </DpForm>
  </DpExpand>

    <DpExpand>
      <DpButton
        v-show="mobileNumberAvailable"
        purpose="secondaryAction"
        class="full-width"
        :disabled="resendDisabled"
        @click="sendAuthenticationSMS">
        <span v-if="resendDisabled">{{
            t("sso.pages.LoginStages.SMSSetupForm.resendSMSInSeconds.buttonLabel", { secondsPlaceholder: countdown })
          }}</span>
        <span v-else>{{ t("sso.pages.LoginStages.SMSSetupForm.resendSMS.buttonLabel") }}</span>
      </DpButton>
    </DpExpand>

    <DpExpand>    
      <DpForm
        v-show="mobileNumberAvailable"
        v-model="loginData"
        @submit="setupSMSAuthentication"
        :showValidationNotification="false"
        :validation="validation"
        >

        <DpInput
        v-model="loginData.SMSOTP"
        :validation="validation.SMSOTP"
        id="SMSOTP"
        autocomplete="off"
        :label="t('sso.pages.LoginStages.SMSSetupForm.SMSOTP.input.label')"
        :placeholder="t('sso.pages.LoginStages.SMSSetupForm.SMSOTP.input.placeholder')" />

          <template #control="{ validate }">
            <DpButton
              purpose="primaryAction"
              size="large"
              @click="validate">
                {{ t("sso.pages.LoginStages.SMSSetupForm.confirmCode.buttonLabel") }}
            </DpButton>
          </template>
      </DpForm>
    </DpExpand>
  </div>
</template>

<i18n>
{
  "ar-AE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "تعذر إرسال الرسالة. يرجى التحقق من دخولك.",
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "لا يمكن حفظ رقم الهاتف المحمول. تحقق من الدخول وحفظ مرة أخرى.",
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {
      "title": "رسالة قصيرة"
    },
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "حفظ وإرسال"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-DZ": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-EG": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "تعذر إرسال الرسالة. يرجى التحقق من دخولك.",
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "لا يمكن حفظ رقم الهاتف المحمول. تحقق من الدخول وحفظ مرة أخرى.",
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {
      "title": "رسالة قصيرة"
    },
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "حفظ وإرسال"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-LB": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثواني"
    }
  },
  "ar-MA": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-SA": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "تعذر إرسال الرسالة. يرجى التحقق من دخولك.",
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "لا يمكن حفظ رقم الهاتف المحمول. تحقق من الدخول وحفظ مرة أخرى.",
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {
      "title": "رسالة قصيرة"
    },
    "resendSMS": {
      "buttonLabel": "أعد الإرسال"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "حفظ وإرسال"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-SY": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-TN": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "خطأ"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "تفعيل المصادقة عبر الرسائل النصية عن طريق إدخال رقم الجوال الصحيح. يتم تأكيد رقم الجوال عن طريق إدخال الرمز التوثيقي المستلم."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "mobileNumber": {
      "input": {
        "label": "رقم الهاتف المحمول بالتنسيق الدولي",
        "placeholder": "أدخل رقم الهاتف المحمول"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "label": "رمز التحقق من الرسائل القصيرة"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثواني"
    }
  },
  "bg-BG": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Успешно действие",
          "message": "Кодът за удостоверяване беше изпратен чрез СМС до {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Съобщението не можа да бъде изпратено. Моля, проверете вашето въвеждане.",
          "title": "грешка"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Мобилният номер не можа да бъде запазен. Проверете въведеното и запазете отново.",
          "title": "грешка"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Активация на удостоверяването по SMS чрез въвеждане на валиден мобилен номер. Потвърждението на мобилния номер се извършва чрез въвеждане на получения код за удостоверяване."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Изпрати отново"
    },
    "mobileNumber": {
      "input": {
        "label": "Мобилен номер в международен формат",
        "placeholder": "Въведете мобилен номер"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Съхраняване и изпращане"
    },
    "confirmCode": {
      "buttonLabel": "Потвърждение"
    },
    "SMSOTP": {
      "input": {
        "label": "Код за удостоверяване от СМС"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Преизпрати след {secondsPlaceholder} секунди"
    }
  },
  "bs-BA": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Uspješno obavljeno",
          "message": "Autentifikacijski kod je poslan putem SMS-a na {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Poruka se nije mogla poslati. Molimo provjerite svoj unos.",
          "title": "Greška"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Broj mobilnog telefona nije mogao biti sačuvan. Provjerite unos i ponovo sačuvajte.",
          "title": "Greška"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivacija SMS autentifikacije unošenjem važećeg broja mobitela. Potvrda broja mobitela vrši se unošenjem primljenog autentifikacijskog koda."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Pošalji ponovo"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilni broj u međunarodnom formatu",
        "placeholder": "Unesite mobilni broj"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Spremite i pošaljite"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdite"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentifikacijski kod iz SMS-a"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ponovno pošalji za {secondsPlaceholder} sekundi"
    }
  },
  "cs-CZ": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Úspěšná akce",
          "message": "Ověřovací kód byl odeslán pomocí SMS na {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Zprávu se nepodařilo odeslat. Zkontrolujte prosím svůj údaj.",
          "title": "Chyba"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Mobilní číslo se nepodařilo uložit. Zkontrolujte zadání a uložte znovu.",
          "title": "Chyba"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivace ověřování pomocí SMS zadáním platného mobilního čísla. Potvrzení mobilního čísla se provede zadáním přijatého ověřovacího kódu."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Poslat znovu"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilní číslo v mezinárodním formátu",
        "placeholder": "Zadejte mobilní číslo"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Uložit a odeslat"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdit"
    },
    "SMSOTP": {
      "input": {
        "label": "Ověřovací kód z SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Znovu odeslat za {secondsPlaceholder} sekundy"
    }
  },
  "da-DK": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Vellykket handling",
          "message": "Godkendelseskoden blev sendt via SMS til {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Meddelelsen kunne ikke sendes. Tjek venligst din post.",
          "title": "Fejl"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Mobilnummeret kunne ikke gemmes. Tjek indtastningen og gem igen.",
          "title": "Fejl"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivering af SMS-godkendelse ved at indtaste et gyldigt mobilnummer. Bekræftelse af mobilnummeret sker ved at indtaste den modtagne godkendelseskode."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Send igen"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer i internationalt format",
        "placeholder": "Indtast mobilnummer"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Gem og send"
    },
    "confirmCode": {
      "buttonLabel": "Bekræft"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentificeringskode fra SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Gensend om {secondsPlaceholder} sekunder"
    }
  },
  "de-DE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS an {mobileNumberPlaceholder} gesendet."
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Fehler"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Die Mobilnummer konnte nicht gespeichert. Eingabe überprüfen und erneut speichern.",
          "title": "Fehler"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivierung der Authentifizierung per SMS durch Eingabe einer gültigen Mobilnummer. Die Bestätigung der Mobilnummer erfolgt durch Eingabe des empfangenen Authentifizierungscodes."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer im internationalen Format",
        "placeholder": "Mobilnummer eingeben"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Speichern und senden"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentifizierungscode aus der SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "de-AT": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS an {mobileNumberPlaceholder} gesendet."
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Fehler"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Die Mobilnummer konnte nicht gespeichert. Eingabe überprüfen und erneut speichern.",
          "title": "Fehler"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivierung der Authentifizierung per SMS durch Eingabe einer gültigen Mobilnummer. Die Bestätigung der Mobilnummer erfolgt durch Eingabe des empfangenen Authentifizierungscodes."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer im internationalen Format",
        "placeholder": "Handynummer eingeben"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Speichern und senden"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentifizierungscode aus der SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "de-CH": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS an {mobileNumberPlaceholder} gesendet."
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Fehler"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Die Mobilnummer konnte nicht gespeichert. Eingabe überprüfen und erneut speichern.",
          "title": "Fehler"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivierung der Authentifizierung per SMS durch Eingabe einer gültigen Mobilnummer. Die Bestätigung der Mobilnummer erfolgt durch Eingabe des empfangenen Authentifizierungscodes."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer im internationalen Format",
        "placeholder": "Handynummer eingeben"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Speichern und senden"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentifizierungscode aus der SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "de-LU": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS an mobileNumberPlaceholder gesendet."
        },
        "asyncError": {
          "title": "Fehler"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Fehler"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivierung der Authentifizierung per SMS durch Eingabe einer gültigen Mobilnummer. Die Bestätigung der Mobilnummer erfolgt durch Eingabe des empfangenen Authentifizierungscodes."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer im internationalen Format",
        "placeholder": "Handynummer eingeben"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentifizierungscode aus der SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "el-GR": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Επιτυχημένη ενέργεια",
          "message": "Ο κωδικός επιβεβαίωσης στάλθηκε μέσω SMS στον {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Δεν ήταν δυνατή η αποστολή του μηνύματος. Ελέγξτε την καταχώρισή σας.",
          "title": "Σφάλμα"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Δεν ήταν δυνατή η αποθήκευση του αριθμού κινητού τηλεφώνου. Ελέγξτε την καταχώριση και αποθηκεύστε ξανά.",
          "title": "Σφάλμα"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Ενεργοποίηση της επιβεβαίωσης μέσω SMS με την εισαγωγή ενός έγκυρου κινητού τηλεφώνου. Η επιβεβαίωση του κινητού τηλεφώνου γίνεται με την εισαγωγή του κωδικού επιβεβαίωσης που έχει ληφθεί."
    },
    "SMSSetupForm": {
      "title": "γραπτό μήνυμα"
    },
    "resendSMS": {
      "buttonLabel": "Στείλε ξανά"
    },
    "mobileNumber": {
      "input": {
        "label": "Αριθμός κινητού στη διεθνή μορφή",
        "placeholder": "Εισάγετε τον αριθμό κινητού"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Αποθήκευση και αποστολή"
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    },
    "SMSOTP": {
      "input": {
        "label": "Κώδικας επαλήθευσης από SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Επανάληψη αποστολής σε {secondsPlaceholder} δευτερόλεπτα"
    }
  },
  "el-CY": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Επιτυχημένη ενέργεια",
          "message": "Ο κωδικός επιβεβαίωσης στάλθηκε μέσω SMS στον {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Δεν ήταν δυνατή η αποστολή του μηνύματος. Ελέγξτε την καταχώρισή σας.",
          "title": "Σφάλμα"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Δεν ήταν δυνατή η αποθήκευση του αριθμού κινητού τηλεφώνου. Ελέγξτε την καταχώριση και αποθηκεύστε ξανά.",
          "title": "Σφάλμα"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Ενεργοποίηση της επιβεβαίωσης μέσω SMS με την εισαγωγή ενός έγκυρου κινητού τηλεφώνου. Η επιβεβαίωση του κινητού τηλεφώνου γίνεται με την εισαγωγή του κωδικού επιβεβαίωσης που έχει ληφθεί."
    },
    "SMSSetupForm": {
      "title": "γραπτό μήνυμα"
    },
    "resendSMS": {
      "buttonLabel": "Στείλε ξανά"
    },
    "mobileNumber": {
      "input": {
        "label": "Αριθμός κινητού στη διεθνή μορφή",
        "placeholder": "Εισάγετε τον αριθμό κινητού"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Αποθήκευση και αποστολή"
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    },
    "SMSOTP": {
      "input": {
        "label": "Κώδικας επαλήθευσης από SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Επανάληψη αποστολής σε {secondsPlaceholder} δευτερόλεπτα"
    }
  },
  "en-GB": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Successful Action",
          "message": "The authentication code was sent via SMS to {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "The message could not be sent. Please check your entry.",
          "title": "Error"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "The mobile number could not be saved. Check entry and save again.",
          "title": "Error"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation of SMS authentication by entering a valid mobile number. The mobile number is confirmed by entering the received authentication code."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Resend"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobile Number in International Format",
        "placeholder": "Enter mobile number"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Save and send"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentication Code from SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    }
  },
  "en-IE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Successful Action",
          "message": "The authentication code was sent via SMS to {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "Error"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Error"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation of SMS authentication by entering a valid mobile number. The mobile number is confirmed by entering the received authentication code."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Resend"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobile Number in International Format",
        "placeholder": "Enter mobile number"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentication Code from SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    }
  },
  "en-US": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Successful Action",
          "message": "The authentication code was sent via SMS to {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "The message could not be sent. Please check your entry.",
          "title": "Error"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "The mobile number could not be saved. Check entry and save again.",
          "title": "Error"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation of SMS authentication by entering a valid mobile number. The mobile number is confirmed by entering the received authentication code."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Resend"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobile Number in International Format",
        "placeholder": "Enter mobile number"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Save and send"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "SMSOTP": {
      "input": {
        "label": "Authentication Code from SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    }
  },
  "es-ES": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Listo",
          "message": "El código de autenticación se envió por SMS a {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "El mensaje no se pudo enviar. Por favor revise su entrada.",
          "title": "Error"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "No se pudo guardar el número de móvil. Verifique la entrada y guarde nuevamente.",
          "title": "Error"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activación de la autenticación por SMS mediante la introducción de un número de teléfono móvil válido. La confirmación del número de móvil se realiza introduciendo el código de autenticación recibido."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Enviar de nuevo"
    },
    "mobileNumber": {
      "input": {
        "label": "Número de móvil en formato internacional",
        "placeholder": "Ingrese el número de móvil"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Guardar y enviar"
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    },
    "SMSOTP": {
      "input": {
        "label": "Código de autenticación del SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Volver a enviar en {secondsPlaceholder} segundos"
    }
  },
  "et-EE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Edukas tegevus",
          "message": "Autentimiskood saadeti SMS-i teel {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Sõnumit ei õnnestunud saata. Palun kontrollige oma sisestust.",
          "title": "Viga"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Mobiilinumbrit ei saanud salvestada. Kontrollige sisestust ja salvestage uuesti.",
          "title": "Viga"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Sõnumituvastuse aktiveerimine, sisestades kehtiva mobiilinumbri. Mobiilinumbri kinnitamine toimub sisestatud autentimiskoodi kaudu."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Saada uuesti"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobiilinumber rahvusvahelises formaadis",
        "placeholder": "Sisestage mobiilinumber"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Salvesta ja saada"
    },
    "confirmCode": {
      "buttonLabel": "Kinnita"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentimiskood SMSist"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Saada uuesti {secondsPlaceholder} sekundi pärast"
    }
  },
  "fi-FI": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Onnistunut toiminta",
          "message": "Vahvistuskoodi lähetettiin tekstiviestillä osoitteeseen {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Viestiä ei voitu lähettää. Tarkista syöttösi.",
          "title": "Virhe"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Matkapuhelinnumeroa ei voitu tallentaa. Tarkista syöte ja tallenna uudelleen.",
          "title": "Virhe"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "SMS-tunnistuksen aktivointi syöttämällä kelvollinen matkapuhelinnumero. Matkapuhelinnumero vahvistetaan syöttämällä saatu tunnistuskoodi."
    },
    "SMSSetupForm": {
      "title": "Tekstiviesti"
    },
    "resendSMS": {
      "buttonLabel": "Lähetä uudestaan"
    },
    "mobileNumber": {
      "input": {
        "label": "Matkapuhelinnumero kansainvälisessä muodossa",
        "placeholder": "Syötä matkapuhelinnumero"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Tallenna ja lähetä"
    },
    "confirmCode": {
      "buttonLabel": "Vahvista"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentikointikoodi tekstiviestistä"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Lähetä uudelleen {secondsPlaceholder} sekunnin kuluttua"
    }
  },
  "fr-BE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Le message n'a pas pu être envoyé. Veuillez vérifier votre saisie.",
          "title": "Erreur"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Le numéro de portable n'a pas pu être enregistré. Vérifiez l'entrée et enregistrez à nouveau.",
          "title": "Erreur"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation de l'authentification par SMS en entrant un numéro de mobile valide. La confirmation du numéro de mobile est faite en entrant le code d'authentification reçu."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Renvoyer"
    },
    "mobileNumber": {
      "input": {
        "label": "Numéro de mobile au format international",
        "placeholder": "Entrez le numéro de portable"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Enregistrer et envoyer"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "label": "Code d'authentification du SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "fr-CH": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "Erreur"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Erreur"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation de l'authentification par SMS en entrant un numéro de mobile valide. La confirmation du numéro de mobile est faite en entrant le code d'authentification reçu."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Renvoyer"
    },
    "mobileNumber": {
      "input": {
        "label": "Numéro de mobile au format international",
        "placeholder": "Entrez le numéro de portable"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "label": "Code d'authentification du SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "fr-FR": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Le message n'a pas pu être envoyé. Veuillez vérifier votre saisie.",
          "title": "Erreur"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Le numéro de portable n'a pas pu être enregistré. Vérifiez l'entrée et enregistrez à nouveau.",
          "title": "Erreur"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation de l'authentification par SMS en entrant un numéro de mobile valide. La confirmation du numéro de mobile se fait par la saisie du code d'authentification reçu."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Envoyer à nouveau"
    },
    "mobileNumber": {
      "input": {
        "label": "Numéro de portable au format international",
        "placeholder": "Entrez le numéro de portable"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Enregistrer et envoyer"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "label": "Code d'authentification du SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "fr-LU": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Le message n'a pas pu être envoyé. Veuillez vérifier votre saisie.",
          "title": "Erreur"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Le numéro de portable n'a pas pu être enregistré. Vérifiez l'entrée et enregistrez à nouveau.",
          "title": "Erreur"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activation de l'authentification par SMS en entrant un numéro de portable valide. La confirmation du numéro de portable se fait par l'entrée du code d'authentification reçu."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Renvoyer"
    },
    "mobileNumber": {
      "input": {
        "label": "Numéro de mobile au format international",
        "placeholder": "Entrez le numéro de portable"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Enregistrer et envoyer"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "label": "Code d'authentification du SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "ga-IE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Gníomh rathúil",
          "message": "Seoladh cód deimhnithe via SMS go {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Ní fhéadfaí an teachtaireacht a sheoladh. Seiceáil d’iontráil le do thoil.",
          "title": "Earráid"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Níorbh fhéidir an uimhir soghluaiste a shábháil. Seiceáil iontráil agus sábháil arís.",
          "title": "Earráid"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Gníomhachtú fíordheimhniú SMS trí uimhir gutháin póca bailí a iontráil. Deimhnítear an uimhir gutháin póca trí an cód fíordheimhniúcháin a fuarthas a iontráil."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Seol arís"
    },
    "mobileNumber": {
      "input": {
        "label": "Uimhir gutháin póca sa bhformat idirnáisiúnta",
        "placeholder": "Cuir isteach uimhir gutháin phóca"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Sábháil agus seol"
    },
    "confirmCode": {
      "buttonLabel": "Deimhnigh"
    },
    "SMSOTP": {
      "input": {
        "label": "Cód deimhnithe ón SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Athseoladh i {secondsPlaceholder} soicind"
    }
  },
  "he-IL": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "פעולה מוצלחת",
          "message": "קוד האימות נשלח באמצעות הודעת טקסט אל {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "לא ניתן היה לשלוח את ההודעה. אנא בדוק את הערך שלך.",
          "title": "שְׁגִיאָה"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "לא ניתן היה לשמור את מספר הנייד. בדוק את הערך ושמור שוב.",
          "title": "שְׁגִיאָה"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "הפעלת אימות SMS על ידי הזנת מספר נייד חוקי. אישור מספר הטלפון הנייד מתבצע על ידי הזנת קוד האימות שהתקבל."
    },
    "SMSSetupForm": {
      "title": "סמס"
    },
    "resendSMS": {
      "buttonLabel": "שלח שוב"
    },
    "mobileNumber": {
      "input": {
        "label": "מספר נייד בפורמט בינלאומי",
        "placeholder": "הכנס מספר נייד"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "שמור ושלח"
    },
    "confirmCode": {
      "buttonLabel": "לְאַשֵׁר"
    },
    "SMSOTP": {
      "input": {
        "label": "קוד אימות מה-SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "שלח מחדש בעוד {secondsPlaceholder} שניות"
    }
  },
  "hi-IN": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "सफल कार्रवाई",
          "message": "प्रमाणीकरण कोड को {mobileNumberPlaceholder} को एसएमएस के माध्यम से भेजा गया था।"
        },
        "asyncError": {
          "title": "त्रुटि"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "त्रुटि"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "एक मान्य मोबाइल नंबर दर्ज करके एसएमएस प्रमाणीकरण का सक्रियन. मोबाइल नंबर की पुष्टि प्राप्त प्रमाणीकरण कोड दर्ज करके की जाती है."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "फिर से भेजें"
    },
    "mobileNumber": {
      "input": {
        "label": "अंतरराष्ट्रीय प्रारूप में मोबाइल नंबर",
        "placeholder": "मोबाइल नंबर दर्ज करें"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "पुष्टि करें"
    },
    "SMSOTP": {
      "input": {
        "label": "एसएमएस से प्रमाणीकरण कोड"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} सेकंड में पुन: भेजें"
    }
  },
  "hr-HR": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Uspješna akcija",
          "message": "Autentifikacijski kod je poslan putem SMS-a na {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Poruka se nije mogla poslati. Molimo provjerite svoj unos.",
          "title": "Pogreška"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Broj mobitela nije bilo moguće spremiti. Provjerite unos i ponovno spremite.",
          "title": "Pogreška"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivacija SMS autentifikacije unosom važećeg broja mobilnog telefona. Potvrda mobilnog broja se obavlja unosom primljenog autentifikacijskog koda."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Pošalji opet"
    },
    "mobileNumber": {
      "input": {
        "label": "Broj mobilnog telefona u međunarodnom formatu",
        "placeholder": "Unesite broj mobitela"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Spremi i pošalji"
    },
    "confirmCode": {
      "buttonLabel": "Potvrda"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentifikacijski kod iz SMS-a"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ponovno pošalji za {secondsPlaceholder} sekundi"
    }
  },
  "hu-HU": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Sikeres művelet",
          "message": "Az hitelesítő kód SMS-ben lett elküldve a {mobileNumberPlaceholder} számra."
        },
        "asyncError": {
          "message": "Az üzenetet nem sikerült elküldeni. Kérjük, ellenőrizze a bejegyzést.",
          "title": "Hiba"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "A mobilszámot nem sikerült elmenteni. Ellenőrizze a bejegyzést, és mentse újra.",
          "title": "Hiba"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "SMS hitelesítés aktiválása érvényes mobil szám megadásával. A mobil szám megerősítése a kapott hitelesítő kód megadásával történik."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Küldd újra"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobiltelefon-szám nemzetközi formátumban",
        "placeholder": "Adja meg a mobil számát"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Mentés és küldés"
    },
    "confirmCode": {
      "buttonLabel": "Megerősít"
    },
    "SMSOTP": {
      "input": {
        "label": "Hitelesítő kód SMS-ből"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Újraküldés {secondsPlaceholder} másodperc múlva"
    }
  },
  "it-CH": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Azione di successo",
          "message": "Il codice di autenticazione è stato inviato tramite SMS a {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "Errore"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Errore"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Attivazione dell'autenticazione SMS inserendo un numero di cellulare valido. La conferma del numero di cellulare avviene inserendo il codice di autenticazione ricevuto."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Invia di nuovo"
    },
    "mobileNumber": {
      "input": {
        "label": "Numero di cellulare in formato internazionale",
        "placeholder": "Inserisci il numero di cellulare"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Confermare"
    },
    "SMSOTP": {
      "input": {
        "label": "Codice di autenticazione dell'SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Invia di nuovo fra {secondsPlaceholder} secondi"
    }
  },
  "it-IT": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Azione riuscita",
          "message": "Il codice di autenticazione è stato inviato tramite SMS a {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Impossibile inviare il messaggio. Si prega di controllare la tua voce.",
          "title": "Errore"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Non è stato possibile salvare il numero di cellulare. Controlla la voce e salva di nuovo.",
          "title": "Errore"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Attivazione dell'autenticazione SMS inserendo un numero di cellulare valido. La conferma del numero di cellulare avviene inserendo il codice di autenticazione ricevuto."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Invia di nuovo"
    },
    "mobileNumber": {
      "input": {
        "label": "Numero di cellulare in formato internazionale",
        "placeholder": "Inserisci il numero di cellulare"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Salva e invia"
    },
    "confirmCode": {
      "buttonLabel": "Conferma"
    },
    "SMSOTP": {
      "input": {
        "label": "Codice di autenticazione dell'SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Invia di nuovo fra {secondsPlaceholder} secondi"
    }
  },
  "ja-JP": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "成功したアクション",
          "message": "認証コードは、SMSで{mobileNumberPlaceholder}に送信されました。"
        },
        "asyncError": {
          "message": "メッセージを送信できませんでした。 入力内容をご確認ください。",
          "title": "エラー"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "携帯電話番号を保存できませんでした。 入力内容を確認し、再度保存してください。",
          "title": "エラー"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "有効な携帯電話番号を入力して、SMS認証を有効にします。携帯電話番号の確認は、受け取った認証コードを入力することによって行われます。"
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "もう一度送信"
    },
    "mobileNumber": {
      "input": {
        "label": "国際形式の携帯電話番号",
        "placeholder": "携帯電話番号を入力してください"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "保存して送信"
    },
    "confirmCode": {
      "buttonLabel": "確認"
    },
    "SMSOTP": {
      "input": {
        "label": "SMSからの認証コード"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} 秒後に再送信"
    }
  },
  "ko-KR": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "성공적인 액션",
          "message": "인증 코드가 {mobileNumberPlaceholder}에게 SMS로 전송되었습니다."
        },
        "asyncError": {
          "title": "오류"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "오류"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "유효한 휴대전화 번호를 입력하여 SMS 인증을 활성화합니다. 휴대전화 번호는 받은 인증 코드를 입력하여 확인됩니다."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "다시 보내기"
    },
    "mobileNumber": {
      "input": {
        "label": "국제 형식의 휴대전화 번호",
        "placeholder": "휴대폰 번호를 입력하세요"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "확인"
    },
    "SMSOTP": {
      "input": {
        "label": "SMS에서 인증 코드"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder}초 후에 재전송"
    }
  },
  "lt-LT": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Sėkmingas veiksmas",
          "message": "Autentifikavimo kodas buvo išsiųstas SMS žinute į {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Nepavyko išsiųsti pranešimo. Patikrinkite savo įrašą.",
          "title": "Klaida"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Nepavyko išsaugoti mobiliojo telefono numerio. Patikrinkite įrašą ir išsaugokite dar kartą.",
          "title": "Klaida"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "SMS autentifikacijos aktyvavimas įvedant galiojantį mobiliojo telefono numerį. Mobilaus telefono numerio patvirtinimas atliekamas įvedant gautą autentifikacijos kodą."
    },
    "SMSSetupForm": {
      "title": "Trumpoji žinutė"
    },
    "resendSMS": {
      "buttonLabel": "Siųsti dar kartą"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobiliojo telefono numeris tarptautiniu formatu",
        "placeholder": "Įveskite mobiliojo telefono numerį"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Išsaugoti ir siųsti"
    },
    "confirmCode": {
      "buttonLabel": "Patvirtinti"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentifikavimo kodas iš SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Persiųsti po {secondsPlaceholder} sekundžių"
    }
  },
  "lv-LV": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Veiksmīga darbība",
          "message": "Autentifikācijas kods tika nosūtīts caur SMS uz {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Ziņojumu nevarēja nosūtīt. Lūdzu, pārbaudiet savu ierakstu.",
          "title": "Kļūda"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Mobilā tālruņa numuru nevarēja saglabāt. Pārbaudiet ierakstu un saglabājiet vēlreiz.",
          "title": "Kļūda"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "SMS autentifikācijas aktivizēšana, ievadot derīgu mobilā tālruņa numuru. Mobilā tālruņa numura apstiprināšana notiek, ievadot saņemto autentifikācijas kodu."
    },
    "SMSSetupForm": {
      "title": "Īsziņa"
    },
    "resendSMS": {
      "buttonLabel": "Sūtīt vēlreiz"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilā telefona numurs starptautiskā formātā",
        "placeholder": "Ievadiet mobilā telefona numuru"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Saglabāt un nosūtīt"
    },
    "confirmCode": {
      "buttonLabel": "Apstiprināt"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentifikācijas kods no SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Atkārtoti nosūtīt pēc {secondsPlaceholder} sekundēm"
    }
  },
  "mt-MT": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Azzjoni ta' suċċess",
          "message": "Il-kodiċi ta' awtentikazzjoni ġie mibgħut permezz ta' SMS lil {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Il-messaġġ ma setax jintbagħat. Jekk jogħġbok iċċekkja d-dħul tiegħek.",
          "title": "Żball"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "In-numru tal-mowbajl ma setax jiġi salvat. Iċċekkja d-dħul u erġa ssejvja.",
          "title": "Żball"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Attivazzjoni tal-aġġornament tal-SMS billi tidħol numru ta' mobbli valida. Il-konferma tan-numru tal-mobbli ssir billi tidħol il-kodiċi ta' aġġornament riċevut."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Ibgħat mill-ġdid"
    },
    "mobileNumber": {
      "input": {
        "label": "Numru tal-mowbajl fil-format internazzjonali",
        "placeholder": "Idħol numru tal-mowbajl"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Ibgħat u ssejvja"
    },
    "confirmCode": {
      "buttonLabel": "Ikkonferma"
    },
    "SMSOTP": {
      "input": {
        "label": "Kodiċi ta' awtentikazzjoni mis-SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ibgħat mill-ġdid fi {secondsPlaceholder} sekondi"
    }
  },
  "nb-NO": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Vellykket handling",
          "message": "Autentiseringskoden ble sendt via SMS til {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "Feil"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Feil"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivering av SMS-autentisering ved å legge inn et gyldig mobilnummer. Mobilnummeret blir bekreftet ved å legge inn den mottatte autentiseringskoden."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Send igjen"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer i internasjonalt format",
        "placeholder": "Skriv inn mobilnummer"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Bekrefte"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentiseringskode fra SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Send på nytt om {secondsPlaceholder} sekunder"
    }
  },
  "nl-BE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Geslaagde actie",
          "message": "De authenticatiecode werd via SMS naar {mobileNumberPlaceholder} gestuurd."
        },
        "asyncError": {
          "title": "Fout"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Fout"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activatie van SMS-authenticatie door het invoeren van een geldig mobiel nummer. De bevestiging van het mobiele nummer gebeurt door het invoeren van de ontvangen authenticatiecode."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Opnieuw verzenden"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobiel nummer in internationaal formaat",
        "placeholder": "Voer mobiel nummer in"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    },
    "SMSOTP": {
      "input": {
        "label": "Authenticatiecode uit de SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Opnieuw verzenden over {secondsPlaceholder} seconden"
    }
  },
  "nl-NL": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Geslaagde actie",
          "message": "De authenticatiecode werd via SMS naar {mobileNumberPlaceholder} gestuurd."
        },
        "asyncError": {
          "message": "Het bericht kon niet worden verzonden. Controleer uw invoer.",
          "title": "Fout"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Het mobiele nummer kon niet worden opgeslagen. Controleer invoer en sla opnieuw op.",
          "title": "Fout"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activatie van SMS-authenticatie door het invoeren van een geldig mobiel nummer. De bevestiging van het mobiele nummer gebeurt door het invoeren van de ontvangen authenticatiecode."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Opnieuw verzenden"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobiel nummer in internationaal formaat",
        "placeholder": "Voer mobiel nummer in"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Opslaan en verzenden"
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    },
    "SMSOTP": {
      "input": {
        "label": "Authenticatiecode uit de SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Opnieuw verzenden over {secondsPlaceholder} seconden"
    }
  },
  "pl-PL": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Czynność zakończona powodzeniem",
          "message": "Kod autoryzacyjny został wysłany za pośrednictwem SMS na {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Nie udało się wysłać wiadomości. Sprawdź swój wpis.",
          "title": "Błąd"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Nie można zapisać numeru telefonu komórkowego. Sprawdź wpis i zapisz ponownie.",
          "title": "Błąd"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktywacja uwierzytelniania SMS przez wprowadzenie poprawnego numeru telefonu komórkowego. Potwierdzenie numeru telefonu komórkowego następuje poprzez wprowadzenie otrzymanego kodu uwierzytelniającego."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Wyślij ponownie"
    },
    "mobileNumber": {
      "input": {
        "label": "Numer telefonu komórkowego w formacie międzynarodowym",
        "placeholder": "Wprowadź numer telefonu komórkowego"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Zapisz i wyślij"
    },
    "confirmCode": {
      "buttonLabel": "Potwierdź"
    },
    "SMSOTP": {
      "input": {
        "label": "Kod autoryzacyjny z SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Wyślij ponownie za {secondsPlaceholder} sekund"
    }
  },
  "pt-PT": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Ação bem-sucedida",
          "message": "O código de autenticação foi enviado por SMS para {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "A mensagem não pôde ser enviada. Por favor, verifique sua entrada.",
          "title": "Erro"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "O número do celular não pôde ser salvo. Verifique a entrada e salve novamente.",
          "title": "Erro"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Ativação da autenticação por SMS através da introdução de um número de telemóvel válido. A confirmação do número de telemóvel é feita através da introdução do código de autenticação recebido."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Envie novamente"
    },
    "mobileNumber": {
      "input": {
        "label": "Número de telemóvel em formato internacional",
        "placeholder": "Insira o número do celular"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Guardar e enviar"
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    },
    "SMSOTP": {
      "input": {
        "label": "Código de autenticação do SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Reenviar em {secondsPlaceholder} segundos"
    }
  },
  "ro-RO": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Acțiune reușită",
          "message": "Codul de autentificare a fost trimis prin SMS la {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Mesajul nu a putut fi trimis. Vă rugăm să verificați intrarea.",
          "title": "Eroare"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Numărul de mobil nu a putut fi salvat. Verificați intrarea și salvați din nou.",
          "title": "Eroare"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Activarea autentificării prin SMS prin introducerea unui număr de mobil valid. Confirmarea numărului de mobil se realizează prin introducerea codului de autentificare primit."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Trimite din nou"
    },
    "mobileNumber": {
      "input": {
        "label": "Numar de telefon mobil în format internațional",
        "placeholder": "Introduceți numărul de mobil"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Salvare și trimitere"
    },
    "confirmCode": {
      "buttonLabel": "Confirmare"
    },
    "SMSOTP": {
      "input": {
        "label": "Codul de autentificare din SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Retrimite în {secondsPlaceholder} secunde"
    }
  },
  "ru-RU": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Успешное действие",
          "message": "Код аутентификации был отправлен по SMS на {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Сообщение не может быть отправлено. Пожалуйста, проверьте вашу запись.",
          "title": "Ошибка"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Не удалось сохранить номер мобильного телефона. Проверьте запись и сохраните снова.",
          "title": "Ошибка"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Активация аутентификации по SMS путем ввода действительного номера мобильного телефона. Подтверждение номера мобильного телефона происходит путем ввода полученного кода аутентификации."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Отправь еще раз"
    },
    "mobileNumber": {
      "input": {
        "label": "Номер мобильного телефона в международном формате",
        "placeholder": "Введите номер мобильного телефона"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Сохранить и отправить"
    },
    "confirmCode": {
      "buttonLabel": "Подтвердить"
    },
    "SMSOTP": {
      "input": {
        "label": "Код аутентификации из SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Отправить снова через {secondsPlaceholder} секунд"
    }
  },
  "sk-SK": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Úspešná akcia",
          "message": "Autentifikačný kód bol odoslaný pomocou SMS na {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Správu sa nepodarilo odoslať. Skontrolujte svoje zadanie.",
          "title": "Chyba"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Mobilné číslo sa nepodarilo uložiť. Skontrolujte zadanie a znova uložte.",
          "title": "Chyba"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivácia autentifikácie SMS zadaním platného mobilného čísla. Potvrdenie mobilného čísla sa vykonáva zadaním prijatého autentifikačného kódu."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Poslať znova"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilné číslo v medzinárodnom formáte",
        "placeholder": "Zadajte mobilné číslo"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Uložiť a odoslať"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdiť"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentifikačný kód z SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Znovu poslať o {secondsPlaceholder} sekundy"
    }
  },
  "sl-SI": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Uspešno opravljeno",
          "message": "Avtentikacijska koda je bila poslana preko SMS na {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Sporočila ni bilo mogoče poslati. Prosimo, preverite svoj vnos.",
          "title": "Napaka"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Številke mobilnega telefona ni bilo mogoče shraniti. Preverite vnos in znova shranite.",
          "title": "Napaka"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivacija SMS avtentikacije z vnosom veljavne mobilne številke. Potrditev mobilne številke se izvede z vnosom prejetega avtentikacijskega kode."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Pošlji ponovno"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilna številka v mednarodni obliki",
        "placeholder": "Vnesite mobilno številko"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Shrani in pošlji"
    },
    "confirmCode": {
      "buttonLabel": "Potrdi"
    },
    "SMSOTP": {
      "input": {
        "label": "Avtentikacijska koda iz SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ponovno pošlji čez {secondsPlaceholder} sekund"
    }
  },
  "sv-SE": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Åtgärd utförd",
          "message": "Autentiseringskoden skickades via SMS till {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Meddelandet kunde inte skickas. Kontrollera ditt bidrag.",
          "title": "Fel"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Mobilnumret kunde inte sparas. Kontrollera inmatningen och spara igen.",
          "title": "Fel"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Aktivering av SMS-autentisering genom att ange ett giltigt mobilnummer. Mobilnumret bekräftas genom att ange den mottagna autentiseringskoden."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Skicka igen"
    },
    "mobileNumber": {
      "input": {
        "label": "Mobilnummer i internationellt format",
        "placeholder": "Ange mobilnummer"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Spara och skicka"
    },
    "confirmCode": {
      "buttonLabel": "Bekräfta"
    },
    "SMSOTP": {
      "input": {
        "label": "Autentiseringskod från SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Skicka igen om {secondsPlaceholder} sekunder"
    }
  },
  "th-TH": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "การดำเนินการที่ประสบความสำเร็จ",
          "message": "รหัสการตรวจสอบถูกส่งผ่าน SMS ไปยัง {mobileNumberPlaceholder}"
        },
        "asyncError": {
          "title": "ความผิดพลาด"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "ความผิดพลาด"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "การเปิดใช้งานการตรวจสอบความถูกต้องทาง SMS โดยการป้อนหมายเลขโทรศัพท์มือถือที่ถูกต้อง การยืนยันหมายเลขโทรศัพท์มือถือทำได้โดยการป้อนรหัสการตรวจสอบความถูกต้องที่ได้รับ"
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "ส่งอีกครั้ง"
    },
    "mobileNumber": {
      "input": {
        "label": "หมายเลขโทรศัพท์มือถือในรูปแบบสากล",
        "placeholder": "ป้อนหมายเลขโทรศัพท์มือถือ"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "ยืนยัน"
    },
    "SMSOTP": {
      "input": {
        "label": "รหัสการยืนยันจาก SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "ส่งใหม่ใน {secondsPlaceholder} วินาที"
    }
  },
  "tr-CY": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Başarılı eylem",
          "message": "Kimlik doğrulama kodu, SMS ile {mobileNumberPlaceholder}'a gönderildi."
        },
        "asyncError": {
          "message": "Mesaj gönderilemedi. Lütfen girişinizi kontrol edin.",
          "title": "Hata"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Cep telefonu numarası kaydedilemedi. Girişi kontrol edin ve tekrar kaydedin.",
          "title": "Hata"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Geçerli bir cep telefonu numarası girerek SMS doğrulamasının etkinleştirilmesi. Cep telefonu numarasının onayı, alınan doğrulama kodunun girilmesiyle gerçekleştirilir."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Tekrar gönderin"
    },
    "mobileNumber": {
      "input": {
        "label": "Uluslararası formatda mobil numara",
        "placeholder": "Cep telefonu numarasını girin"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Kaydet ve gönder"
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    },
    "SMSOTP": {
      "input": {
        "label": "SMS'den doğrulama kodu"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} saniye sonra yeniden gönder"
    }
  },
  "tr-TR": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Başarılı eylem",
          "message": "Kimlik doğrulama kodu, SMS ile {mobileNumberPlaceholder}'a gönderildi."
        },
        "asyncError": {
          "message": "Mesaj gönderilemedi. Lütfen girişinizi kontrol edin.",
          "title": "Hata"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Cep telefonu numarası kaydedilemedi. Girişi kontrol edin ve tekrar kaydedin.",
          "title": "Hata"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Geçerli bir cep telefonu numarası girerek SMS doğrulamasının etkinleştirilmesi. Cep telefonu numarasının onayı, alınan doğrulama kodunun girilmesiyle gerçekleştirilir."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Tekrar gönderin"
    },
    "mobileNumber": {
      "input": {
        "label": "Uluslararası formatda mobil numara",
        "placeholder": "Cep telefonu numarasını girin"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Kaydet ve gönder"
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    },
    "SMSOTP": {
      "input": {
        "label": "SMS'den doğrulama kodu"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} saniye sonra yeniden gönder"
    }
  },
  "uk-UA": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Успішна дія",
          "message": "Код автентифікації був відправлений через SMS на {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Помилка"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "Не вдалося зберегти номер мобільного телефону. Перевірте введення та збережіть ще раз.",
          "title": "Помилка"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Активація аутентифікації за допомогою SMS шляхом введення дійсного мобільного номера. Підтвердження мобільного номера відбувається за допомогою введення отриманого коду аутентифікації."
    },
    "SMSSetupForm": {
      "title": "SMS"
    },
    "resendSMS": {
      "buttonLabel": "Надіслати знову"
    },
    "mobileNumber": {
      "input": {
        "label": "Номер мобільного телефону в міжнародному форматі",
        "placeholder": "Введіть номер мобільного"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "Зберегти та відправити"
    },
    "confirmCode": {
      "buttonLabel": "Підтвердити"
    },
    "SMSOTP": {
      "input": {
        "label": "Код автентифікації з SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Надіслати знову за {secondsPlaceholder} секунд"
    }
  },
  "vi-VN": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "Hành động thành công",
          "message": "Mã xác thực đã được gửi qua SMS đến {mobileNumberPlaceholder}."
        },
        "asyncError": {
          "title": "Lỗi"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "title": "Lỗi"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "Kích hoạt xác thực SMS bằng cách nhập một số di động hợp lệ. Xác nhận số di động được thực hiện bằng cách nhập mã xác thực đã nhận."
    },
    "SMSSetupForm": {},
    "resendSMS": {
      "buttonLabel": "Gửi lại"
    },
    "mobileNumber": {
      "input": {
        "label": "Số điện thoại di động theo định dạng quốc tế",
        "placeholder": "Nhập số điện thoại di động"
      }
    },
    "saveMobileNumber": {},
    "confirmCode": {
      "buttonLabel": "Xác nhận"
    },
    "SMSOTP": {
      "input": {
        "label": "Mã xác thực từ SMS"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Gửi lại sau {secondsPlaceholder} giây"
    }
  },
  "zh-CN": {
    "notification": {
      "sendSMS": {
        "success": {
          "title": "操作成功",
          "message": "身份验证代码已通过短信发送至{mobileNumberPlaceholder}。"
        },
        "asyncError": {
          "message": "无法发送消息。 请检查您的输入。",
          "title": "错误"
        }
      },
      "saveMobileNumber": {
        "asyncError": {
          "message": "无法保存手机号码。 检查条目并再次保存。",
          "title": "错误"
        }
      }
    },
    "pleaseEnterMobileNumber": {
      "label": "通过输入有效的手机号码激活短信认证。通过输入接收到的认证代码来确认手机号码。"
    },
    "SMSSetupForm": {
      "title": "短信"
    },
    "resendSMS": {
      "buttonLabel": "重新发送"
    },
    "mobileNumber": {
      "input": {
        "label": "国际格式的手机号码",
        "placeholder": "输入手机号码"
      }
    },
    "saveMobileNumber": {
      "buttonLabel": "保存并发送"
    },
    "confirmCode": {
      "buttonLabel": "确认"
    },
    "SMSOTP": {
      "input": {
        "label": "来自短信的认证码"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} 秒后重新发送"
    }
  }
}
</i18n>
